import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, ArrowRight, Eye } from "lucide-react";

import { getMyMarksAssignments } from "../../api/markschedule/marksPortalApi";
import AssignmentStatusBadge from "../../components/markschedule/AssignmentStatusBadge";
import {
  formatDate,
  pickList,
  toId,
} from "../../utils/markschedule/marksHelpers";
import { isAssignmentLocked } from "../../utils/markschedule/marksConstants";
import {
  Button,
  Select,
  Card,
  CardContent,
  Alert,
  EmptyState,
  Skeleton,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "In progress", value: "IN_PROGRESS" },
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Locked", value: "LOCKED" },
];

const MyMarksAssignments = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getMyMarksAssignments({
          status: statusFilter || undefined,
        });
        setAssignments(pickList(res, ["assignments", "data"]));
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [statusFilter]);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header & Filter */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            My Marks Tasks
          </h1>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
            Manage and submit assigned exam marks for your subjects and sections.
          </p>
        </div>
        <div className="w-full sm:w-56">
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
          />
        </div>
      </div>

      {/* Error Alert */}
      

      {/* Content Grid */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton height={88} width="100%" />
          <Skeleton height={88} width="100%" />
          <Skeleton height={88} width="100%" />
        </div>
      ) : assignments.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={ClipboardList}
              title="No assignments found"
              description="You have no assigned marks tasks matching the selected status filter."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {assignments.map((a) => {
            const id = toId(a);
            const locked = isAssignmentLocked(a.status);
            return (
              <Card
                key={id}
                className="hover:border-neutral-300 transition-colors"
              >
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-base">
                        {a.examName || a.examId?.name || "Exam"}
                      </h3>
                      <AssignmentStatusBadge status={a.status} />
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-medium text-neutral-700 dark:text-neutral-200">
                        {a.subjectId?.name || "Subject"}
                      </span>
                      <span>·</span>
                      <span>
                        {a.classId?.className || "Class"} ·{" "}
                        {a.sectionId?.sectionName || "Section"}
                      </span>
                      <span>·</span>
                      <span>Max {a.maxMarks}</span>
                      {a.examDate && (
                        <>
                          <span>·</span>
                          <span>{formatDate(a.examDate)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center shrink-0">
                    <Button
                      type="button"
                      variant={locked ? "secondary" : "outline-primary"}
                      size="sm"
                      onClick={() => navigate(`/staff-panel/marks/entry/${id}`)}
                      rightIcon={
                        locked ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <ArrowRight className="h-4 w-4" />
                        )
                      }
                      className="w-full sm:w-auto"
                    >
                      {locked ? "View" : "Enter Marks"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyMarksAssignments;